/**
 * Part 16 — Admin page for managing per-org custom status sets.
 *
 * Two views in one page:
 *   - List of all sets, split by entity_type.
 *   - Edit form (also handles create) — name/description/default toggle +
 *     a row of status entries with key/label/color/canonical/terminal.
 *
 * Save flow:
 *   PUT replaces statuses wholesale on the backend (full-replace semantics),
 *   so we round-trip the entire list. After save we invalidate the cache
 *   used by useStatusOptions so any open project/task forms pick up changes
 *   without a reload.
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Plus, Save, Trash2, ArrowUp, ArrowDown, X } from 'lucide-react'

import {
  CANONICAL_PROJECT,
  CANONICAL_TASK,
  statusSetsService,
  canonicalOptionsFor,
  type OrgStatus,
  type OrgStatusSet,
  type StatusEntityType,
} from '../services/statusSets'
import { invalidateStatusOptions } from '../hooks/useStatusOptions'

type Mode = { kind: 'list' } | { kind: 'edit'; set: OrgStatusSet | null }

const ENTITY_TABS: Array<{ id: StatusEntityType; label: string }> = [
  { id: 'project', label: 'Projects' },
  { id: 'task', label: 'Tasks' },
]

export default function StatusSetsAdminPage() {
  const [entityType, setEntityType] = useState<StatusEntityType>('project')
  const [sets, setSets] = useState<OrgStatusSet[]>([])
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })

  const load = async () => {
    setLoading(true)
    try {
      const rows = await statusSetsService.list({ entity_type: entityType })
      setSets(rows)
    } catch (e) {
      toast.error('Failed to load status sets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType])

  const startCreate = () =>
    setMode({
      kind: 'edit',
      set: null,
    })

  const startEdit = (s: OrgStatusSet) => setMode({ kind: 'edit', set: s })

  if (mode.kind === 'edit') {
    return (
      <StatusSetEditor
        entityType={entityType}
        initial={mode.set}
        onCancel={() => setMode({ kind: 'list' })}
        onSaved={() => {
          setMode({ kind: 'list' })
          invalidateStatusOptions(entityType)
          load()
        }}
      />
    )
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#102a43]">Status sets</h1>
          <p className="text-sm text-[#486581] mt-1">
            Define the statuses your organization uses for projects and tasks.
            One set marked default applies to new records.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#102a43] text-white text-sm rounded hover:bg-[#243b53]"
        >
          <Plus className="w-4 h-4" />
          New status set
        </button>
      </div>

      <div className="flex gap-2 mb-4 border-b border-[#bcccdc]">
        {ENTITY_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setEntityType(t.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              entityType === t.id
                ? 'border-[#102a43] text-[#102a43] font-medium'
                : 'border-transparent text-[#486581] hover:text-[#102a43]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#486581]">Loading…</div>
      ) : sets.length === 0 ? (
        <EmptyState entityType={entityType} onCreate={startCreate} />
      ) : (
        <div className="space-y-3">
          {sets.map((s) => (
            <SetCard key={s.id} set={s} onEdit={() => startEdit(s)} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  )
}

function EmptyState({
  entityType,
  onCreate,
}: {
  entityType: StatusEntityType
  onCreate: () => void
}) {
  const sample = entityType === 'project' ? 'Drafting → Filed' : 'Scheduled → Inspected'
  return (
    <div className="bg-[#f0f4f8] border border-[#bcccdc] rounded p-8 text-center">
      <p className="text-[#102a43] font-medium mb-1">
        No custom {entityType} status sets yet
      </p>
      <p className="text-sm text-[#486581] mb-4">
        Your org will use the default statuses until you define your own.
        e.g. legal teams use {sample}.
      </p>
      <button
        onClick={onCreate}
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#102a43] text-white text-sm rounded hover:bg-[#243b53]"
      >
        <Plus className="w-4 h-4" />
        Create your first set
      </button>
    </div>
  )
}

function SetCard({
  set,
  onEdit,
  onChanged,
}: {
  set: OrgStatusSet
  onEdit: () => void
  onChanged: () => void
}) {
  const handleDelete = async () => {
    if (!window.confirm(`Delete status set "${set.name}"? It will be soft-deleted.`))
      return
    try {
      await statusSetsService.remove(set.id)
      toast.success('Status set deleted')
      onChanged()
    } catch {
      toast.error('Failed to delete')
    }
  }
  return (
    <div className="bg-white border border-[#bcccdc] rounded p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-medium text-[#102a43]">{set.name}</h3>
            {set.is_default && (
              <span className="px-2 py-0.5 text-xs rounded bg-[#102a43] text-white">
                Default
              </span>
            )}
          </div>
          {set.description && (
            <p className="text-sm text-[#486581] mt-1">{set.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-sm border border-[#bcccdc] rounded hover:border-[#486581]"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            aria-label="Delete"
            className="p-2 text-[#829ab1] hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {set.statuses.map((s) => (
          <span
            key={s.id ?? s.key}
            className="inline-flex items-center gap-2 px-2.5 py-1 text-xs rounded border border-[#bcccdc]"
            style={s.color ? { borderColor: s.color, color: s.color } : undefined}
          >
            {s.color && (
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: s.color }}
              />
            )}
            {s.label}
            {s.is_terminal && (
              <span className="text-[10px] uppercase tracking-wider text-[#829ab1]">
                end
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  )
}

function StatusSetEditor({
  entityType,
  initial,
  onCancel,
  onSaved,
}: {
  entityType: StatusEntityType
  initial: OrgStatusSet | null
  onCancel: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [isDefault, setIsDefault] = useState(initial?.is_default ?? false)
  const [statuses, setStatuses] = useState<OrgStatus[]>(
    initial?.statuses.length
      ? [...initial.statuses].sort((a, b) => a.position - b.position)
      : canonicalOptionsFor(entityType),
  )
  const [saving, setSaving] = useState(false)

  const canonicalChoices = useMemo(
    () => (entityType === 'project' ? CANONICAL_PROJECT : CANONICAL_TASK),
    [entityType],
  )

  const addRow = () => {
    setStatuses((s) => [
      ...s,
      {
        key: '',
        label: '',
        canonical: canonicalChoices[0],
        position: s.length,
        is_terminal: false,
        color: null,
      },
    ])
  }

  const removeRow = (idx: number) => {
    setStatuses((s) => s.filter((_, i) => i !== idx).map((row, i) => ({ ...row, position: i })))
  }

  const moveRow = (idx: number, dir: -1 | 1) => {
    setStatuses((s) => {
      const next = [...s]
      const target = idx + dir
      if (target < 0 || target >= next.length) return s
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next.map((row, i) => ({ ...row, position: i }))
    })
  }

  const updateRow = (idx: number, patch: Partial<OrgStatus>) => {
    setStatuses((s) =>
      s.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    )
  }

  const validate = (): string | null => {
    if (!name.trim()) return 'Name is required'
    if (statuses.length === 0) return 'Add at least one status'
    const keys = new Set<string>()
    for (const s of statuses) {
      if (!s.key.trim()) return 'Every status needs a key'
      if (!s.label.trim()) return `Status "${s.key}" needs a label`
      if (keys.has(s.key)) return `Duplicate key "${s.key}"`
      if (!(canonicalChoices as readonly string[]).includes(s.canonical))
        return `Status "${s.key}" has an invalid canonical value`
      keys.add(s.key)
    }
    return null
  }

  const handleSave = async () => {
    const err = validate()
    if (err) {
      toast.error(err)
      return
    }
    setSaving(true)
    try {
      if (initial) {
        await statusSetsService.update(initial.id, {
          name: name.trim(),
          description: description?.trim() || null,
          is_default: isDefault,
          statuses,
        })
      } else {
        await statusSetsService.create({
          entity_type: entityType,
          name: name.trim(),
          description: description?.trim() || null,
          is_default: isDefault,
          statuses,
        })
      }
      toast.success(initial ? 'Status set updated' : 'Status set created')
      onSaved()
    } catch (e: unknown) {
      const detail =
        typeof e === 'object' && e !== null && 'response' in e
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (e as any).response?.data?.detail
          : null
      toast.error(typeof detail === 'string' ? detail : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <button
            onClick={onCancel}
            className="text-sm text-[#486581] hover:text-[#102a43] mb-1"
          >
            ← Back to list
          </button>
          <h1 className="text-2xl font-semibold text-[#102a43]">
            {initial ? 'Edit status set' : 'New status set'}
          </h1>
          <p className="text-sm text-[#486581] mt-1">
            For <strong>{entityType}s</strong>. Every status maps to a canonical
            bucket so analytics keep working across sets.
          </p>
        </div>
      </div>

      <div className="bg-white border border-[#bcccdc] rounded p-6 space-y-4 mb-6">
        <label className="block">
          <span className="text-sm text-[#486581] mb-1 block">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              entityType === 'project' ? 'Legal matters' : 'Construction tasks'
            }
            className="w-full px-3 py-2 border border-[#bcccdc] rounded focus:outline-none focus:border-[#486581]"
          />
        </label>
        <label className="block">
          <span className="text-sm text-[#486581] mb-1 block">
            Description (optional)
          </span>
          <input
            value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="When the firm should use this set"
            className="w-full px-3 py-2 border border-[#bcccdc] rounded focus:outline-none focus:border-[#486581]"
          />
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <span className="text-sm text-[#486581]">
            Make this the default {entityType} status set for the org
          </span>
        </label>
      </div>

      <div className="bg-white border border-[#bcccdc] rounded p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-medium text-[#102a43]">Statuses</h2>
          <button
            onClick={addRow}
            className="inline-flex items-center gap-1 text-sm text-[#102a43] hover:underline"
          >
            <Plus className="w-4 h-4" />
            Add status
          </button>
        </div>

        <div className="space-y-2">
          {statuses.map((s, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 items-center bg-[#f7f8f9] border border-[#bcccdc] rounded p-2"
            >
              <div className="col-span-1 flex flex-col items-center">
                <button
                  aria-label="Move up"
                  onClick={() => moveRow(idx, -1)}
                  disabled={idx === 0}
                  className="text-[#486581] hover:text-[#102a43] disabled:opacity-30"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  aria-label="Move down"
                  onClick={() => moveRow(idx, 1)}
                  disabled={idx === statuses.length - 1}
                  className="text-[#486581] hover:text-[#102a43] disabled:opacity-30"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
              <input
                value={s.key}
                onChange={(e) => updateRow(idx, { key: e.target.value })}
                placeholder="key (e.g. drafting)"
                className="col-span-3 px-2 py-1.5 border border-[#bcccdc] rounded text-sm bg-white"
              />
              <input
                value={s.label}
                onChange={(e) => updateRow(idx, { label: e.target.value })}
                placeholder="Display label"
                className="col-span-3 px-2 py-1.5 border border-[#bcccdc] rounded text-sm bg-white"
              />
              <input
                type="color"
                value={s.color || '#102a43'}
                onChange={(e) => updateRow(idx, { color: e.target.value })}
                title="Badge color"
                className="col-span-1 h-9 w-full border border-[#bcccdc] rounded bg-white cursor-pointer"
              />
              <select
                value={s.canonical}
                onChange={(e) => updateRow(idx, { canonical: e.target.value })}
                className="col-span-2 px-2 py-1.5 border border-[#bcccdc] rounded text-sm bg-white"
              >
                {canonicalChoices.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <label className="col-span-1 flex items-center gap-1 text-xs text-[#486581]">
                <input
                  type="checkbox"
                  checked={s.is_terminal}
                  onChange={(e) => updateRow(idx, { is_terminal: e.target.checked })}
                />
                End
              </label>
              <button
                aria-label="Remove status"
                onClick={() => removeRow(idx)}
                className="col-span-1 text-[#829ab1] hover:text-red-600 flex justify-end"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-[#486581] hover:text-[#102a43]"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#102a43] text-white text-sm rounded hover:bg-[#243b53] disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
